import type { FeeTariffResponse, FeeTariffRowRequest } from '@api/generated/types.gen';

/** Тип договора в тарифной сетке. */
export type AgreementType = 'LOAN' | 'INSTALLMENT';

export const AGREEMENT_TYPES: AgreementType[] = ['LOAN', 'INSTALLMENT'];

/**
 * Редактируемая строка сетки. Суммы в тийинах, `null` — поле не заполнено.
 * `key` — локальный идентификатор строки для React и привязки ошибок.
 */
export type TariffRow = {
  key: string;
  minTiyin: number | null;
  maxTiyin: number | null;
  feeTiyin: number | null;
  updatedAt?: string;
};

/** Сетка в редакторе: строки по каждому типу договора. */
export type TariffGrid = Record<AgreementType, TariffRow[]>;

/**
 * Ошибки клиентской валидации: ключи i18n (`feeTariffs:validation.*`)
 * по строкам (`rows`, key строки → коды) и по сетке в целом (`global`).
 */
export type GridErrors = {
  rows: Record<string, string[]>;
  global: string[];
};

let keyCounter = 0;

/** Новый локальный ключ строки. */
export function nextRowKey(): string {
  keyCounter += 1;
  return `row-${String(keyCounter)}`;
}

/** Тийины → сумы (для отображения; 1 сум = 100 тийинов). */
export function tiyinToSum(tiyin: number): number {
  return tiyin / 100;
}

/** Сумы → тийины (целое; ввод допускает до 2 знаков после запятой). */
export function sumToTiyin(sum: number): number {
  return Math.round(sum * 100);
}

/** Собирает редактируемую сетку из ответа API (сортировка по нижней границе). */
export function gridFromResponse(rows: FeeTariffResponse[]): TariffGrid {
  const grid: TariffGrid = { LOAN: [], INSTALLMENT: [] };
  for (const r of rows) {
    const type = r.agreementType;
    if (type !== 'LOAN' && type !== 'INSTALLMENT') continue;
    grid[type].push({
      key: nextRowKey(),
      minTiyin: r.amountMinTiyin ?? null,
      maxTiyin: r.amountMaxTiyin ?? null,
      feeTiyin: r.feeTiyin ?? null,
      ...(r.updatedAt !== undefined && { updatedAt: r.updatedAt }),
    });
  }
  for (const type of AGREEMENT_TYPES) {
    grid[type].sort((a, b) => (a.minTiyin ?? 0) - (b.minTiyin ?? 0));
  }
  return grid;
}

/** Разворачивает сетку в строки PUT-запроса (LOAN, затем INSTALLMENT). */
export function gridToRequestRows(grid: TariffGrid): FeeTariffRowRequest[] {
  return AGREEMENT_TYPES.flatMap((type) =>
    grid[type].map((row) => ({
      agreementType: type,
      amountMinTiyin: row.minTiyin ?? 0,
      amountMaxTiyin: row.maxTiyin ?? 0,
      feeTiyin: row.feeTiyin ?? 0,
    })),
  );
}

/**
 * Клиентская валидация сетки — зеркалит серверные правила
 * (`FEE_TARIFF_GRID_INVALID`), чтобы подсветить проблемную строку до отправки:
 * - оба типа договора присутствуют;
 * - диапазоны каждого типа начинаются с 0 и идут сплошняком
 *   (следующий `min` = предыдущий `max` + 1);
 * - `max >= min`, `fee > 0`, все поля заполнены.
 */
export function validateGrid(grid: TariffGrid): GridErrors {
  const errors: GridErrors = { rows: {}, global: [] };
  const addRowError = (key: string, code: string): void => {
    (errors.rows[key] ??= []).push(code);
  };

  for (const type of AGREEMENT_TYPES) {
    const rows = grid[type];
    if (rows.length === 0) {
      errors.global.push(`missingType.${type}`);
      continue;
    }
    for (const [i, row] of rows.entries()) {
      if (row.minTiyin === null || row.maxTiyin === null || row.feeTiyin === null) {
        addRowError(row.key, 'incomplete');
        continue;
      }
      if (row.minTiyin < 0) addRowError(row.key, 'negative');
      if (i === 0) {
        if (row.minTiyin !== 0) addRowError(row.key, 'firstNotZero');
      } else {
        const prevMax = rows[i - 1]?.maxTiyin ?? null;
        if (prevMax !== null && row.minTiyin !== prevMax + 1) {
          addRowError(row.key, row.minTiyin > prevMax + 1 ? 'gap' : 'overlap');
        }
      }
      if (row.maxTiyin < row.minTiyin) addRowError(row.key, 'maxBelowMin');
      if (row.feeTiyin <= 0) addRowError(row.key, 'feeNotPositive');
    }
  }
  return errors;
}

/** Истинно, когда валидация не нашла ни одной проблемы. */
export function isGridValid(errors: GridErrors): boolean {
  return errors.global.length === 0 && Object.keys(errors.rows).length === 0;
}

/**
 * Пересчитывает нижние границы по порядку строк: первая с 0, каждая следующая —
 * `max` предыдущей + 1. Верхние границы и комиссии не трогает.
 */
export function renumberMins(grid: TariffGrid): TariffGrid {
  const next: TariffGrid = { LOAN: [], INSTALLMENT: [] };
  for (const type of AGREEMENT_TYPES) {
    let min = 0;
    next[type] = grid[type].map((row) => {
      const fixed = { ...row, minTiyin: min };
      min = (row.maxTiyin ?? min) + 1;
      return fixed;
    });
  }
  return next;
}

/** Сериализация значимых полей сетки — для сравнения «есть ли изменения». */
export function gridFingerprint(grid: TariffGrid): string {
  return JSON.stringify(
    AGREEMENT_TYPES.map((type) =>
      grid[type].map((r) => [r.minTiyin, r.maxTiyin, r.feeTiyin]),
    ),
  );
}

const CSV_COLUMNS = ['agreementtype', 'amountmintiyin', 'amountmaxtiyin', 'feetiyin'] as const;

/**
 * Результат разбора CSV: сетка либо код ошибки (`feeTariffs:import.errors.*`)
 * с номером строки файла.
 */
export type CsvParseResult =
  | { ok: true; grid: TariffGrid }
  | { ok: false; error: string; line?: number };

/**
 * Разбирает CSV с колонками API-формата: `agreementType`, `amountMinTiyin`,
 * `amountMaxTiyin`, `feeTiyin` (регистр и порядок колонок не важны,
 * разделитель — `;`, `,` или таб; первая строка — заголовок).
 *
 * Значения — в тийинах, как в API. Ошибка возвращается с номером строки файла.
 */
export function parseTariffCsv(text: string): CsvParseResult {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== '');
  if (lines.length < 2) {
    return { ok: false, error: 'empty' };
  }

  const headerLine = lines[0] ?? '';
  const delimiter = [';', ',', '\t'].find((d) => headerLine.includes(d));
  if (!delimiter) {
    return { ok: false, error: 'badHeader', line: 1 };
  }
  const header = headerLine.split(delimiter).map((h) => h.trim().toLowerCase());
  const typeIdx = header.indexOf(CSV_COLUMNS[0]);
  const minIdx = header.indexOf(CSV_COLUMNS[1]);
  const maxIdx = header.indexOf(CSV_COLUMNS[2]);
  const feeIdx = header.indexOf(CSV_COLUMNS[3]);
  if ([typeIdx, minIdx, maxIdx, feeIdx].includes(-1)) {
    return { ok: false, error: 'badHeader', line: 1 };
  }

  const grid: TariffGrid = { LOAN: [], INSTALLMENT: [] };
  for (let i = 1; i < lines.length; i++) {
    const cells = (lines[i] ?? '').split(delimiter).map((c) => c.trim());
    const type = (cells[typeIdx] ?? '').toUpperCase();
    if (type !== 'LOAN' && type !== 'INSTALLMENT') {
      return { ok: false, error: 'badType', line: i + 1 };
    }
    const min = Number(cells[minIdx] ?? '');
    const max = Number(cells[maxIdx] ?? '');
    const fee = Number(cells[feeIdx] ?? '');
    if ([min, max, fee].some((n) => !Number.isFinite(n) || !Number.isInteger(n))) {
      return { ok: false, error: 'badNumber', line: i + 1 };
    }
    grid[type].push({
      key: nextRowKey(),
      minTiyin: min,
      maxTiyin: max,
      feeTiyin: fee,
    });
  }
  for (const type of AGREEMENT_TYPES) {
    grid[type].sort((a, b) => (a.minTiyin ?? 0) - (b.minTiyin ?? 0));
  }
  return { ok: true, grid };
}
